import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { FileText, MessageSquare, LogOut, Users, User, BookOpen, ClipboardList, ChevronRight, BarChart3, Archive, ArchiveRestore } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CollaboratorDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchProjects = async (includeArchived) => {
    try {
      const res = await axios.get(`${API}/collaborator/me/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_archived: includeArchived }
      });
      setItems(res.data.projects || []);
      setArchivedCount(res.data.archived_count || 0);
      setActiveCount(res.data.active_count || 0);
    } catch (err) {
      toast.error('Failed to load assigned projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchProjects(showArchived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showArchived]);

  const archiveProject = async (projectId) => {
    if (!window.confirm('Archive this project? It will be hidden from your active dashboard. You can restore it anytime.')) return;
    setBusyId(projectId);
    try {
      await axios.post(`${API}/collaborator/projects/${projectId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project archived');
      fetchProjects(showArchived);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive');
    } finally {
      setBusyId(null);
    }
  };

  const unarchiveProject = async (projectId) => {
    setBusyId(projectId);
    try {
      await axios.post(`${API}/collaborator/projects/${projectId}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project restored');
      fetchProjects(showArchived);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    } finally {
      setBusyId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/collaborator-login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-rose-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-rose-700 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">StatsXY Supervisor</div>
                <div className="text-xs text-slate-500">{user?.email}</div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="collab-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-serif font-bold text-slate-900 mb-1">Supervisor Dashboard</h1>
            <p className="text-slate-600">
              {showArchived
                ? `Showing archived dissertations (${archivedCount})`
                : `${activeCount} active dissertation${activeCount !== 1 ? 's' : ''}${archivedCount > 0 ? ` · ${archivedCount} archived` : ''}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-rose-50 border border-rose-200 text-rose-800 rounded-full text-sm font-medium">
              <Users className="w-4 h-4" />
              {items.length} shown
            </div>
            {archivedCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowArchived(!showArchived)}
                data-testid="toggle-archived-btn"
              >
                {showArchived ? (
                  <><ArchiveRestore className="w-4 h-4 mr-1.5" />Show active</>
                ) : (
                  <><Archive className="w-4 h-4 mr-1.5" />Show archived ({archivedCount})</>
                )}
              </Button>
            )}
          </div>
        </div>

        {items.length === 0 && (
          <div className="card-dashboard text-center py-16" data-testid="collab-empty-state">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">
              {showArchived
                ? 'No archived dissertations.'
                : 'No active dissertations assigned to your supervisor account.'}
            </p>
            {!showArchived && archivedCount === 0 && (
              <p className="text-xs text-slate-500 mt-2">Please contact the administrator if you were expecting an assignment.</p>
            )}
          </div>
        )}

        <div className="space-y-5">
          {items.map((item) => {
            const p = item.project;
            const o = item.owner;
            const isArchived = item.archived;
            return (
              <div
                key={p.id}
                className={`card-dashboard ${isArchived ? 'opacity-75 border-slate-300' : ''}`}
                data-testid={`collab-project-${p.id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-xs uppercase tracking-wide text-rose-600 font-semibold">Assigned dissertation</div>
                      {isArchived && (
                        <span className="px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full text-xs font-medium">
                          <Archive className="inline w-3 h-3 mr-1" />
                          Archived
                        </span>
                      )}
                    </div>
                    <h2 className="text-xl font-serif font-semibold text-slate-900 truncate">{p.title}</h2>
                    <div className="text-sm text-slate-500 mt-1 capitalize">{p.research_type} study</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="px-2.5 py-1 bg-rose-50 border border-rose-200 text-rose-800 rounded-full text-xs font-medium whitespace-nowrap">
                      <MessageSquare className="inline w-3 h-3 mr-1" />
                      {item.comment_count} comment{item.comment_count !== 1 ? 's' : ''}
                    </div>
                    {isArchived ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => unarchiveProject(p.id)}
                        disabled={busyId === p.id}
                        data-testid={`restore-${p.id}-btn`}
                      >
                        <ArchiveRestore className="w-3.5 h-3.5 mr-1" />
                        Restore
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => archiveProject(p.id)}
                        disabled={busyId === p.id}
                        className="text-slate-600 hover:text-slate-900"
                        data-testid={`archive-${p.id}-btn`}
                        title="Archive this dissertation (hide from active list)"
                      >
                        <Archive className="w-3.5 h-3.5 mr-1" />
                        Archive
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-slate-500">Student</div>
                      <div className="text-slate-900 font-medium">{o?.full_name || '—'}</div>
                      <div className="text-xs text-slate-500">{o?.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-slate-500">Research aim</div>
                      <div className="text-slate-700 line-clamp-2">{p.research_aim || '—'}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 pt-5 border-t border-slate-100 grid sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => navigate(`/project/${p.id}/proposal`)}
                    className="flex items-center justify-between px-4 py-3 bg-rose-700 hover:bg-rose-800 text-white rounded-lg transition-colors"
                    data-testid={`review-proposal-${p.id}-btn`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      <span className="text-sm font-medium">Research Proposal</span>
                    </div>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate(`/project/${p.id}/analysis-review`)}
                    className="flex items-center justify-between px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                    data-testid={`review-analysis-${p.id}-btn`}
                  >
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      <span className="text-sm font-medium">Analysis Results</span>
                    </div>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate(`/project/${p.id}/literature-review`)}
                    className="flex items-center justify-between px-4 py-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-lg transition-colors"
                    data-testid={`review-litreview-${p.id}-btn`}
                  >
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-indigo-600" />
                      <span className="text-sm font-medium">Literature Review</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </button>
                  <button
                    onClick={() => navigate(`/project/${p.id}/methodology`)}
                    className="flex items-center justify-between px-4 py-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-lg transition-colors"
                    data-testid={`review-methodology-${p.id}-btn`}
                  >
                    <div className="flex items-center gap-2">
                      <ClipboardList className="w-4 h-4 text-violet-600" />
                      <span className="text-sm font-medium">Methodology</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default CollaboratorDashboard;
